<script setup>
import {computed, onMounted} from "vue";
import {useRoute} from "vue-router";
import dayjs from "dayjs";

import {useTicketStore} from "../../store/tickets.js";
import EmptyState from "../../components/global/EmptyState.vue";
import OrderHistoryDay from "../../components/orders/OrderHistoryDay.vue";
import CallNoteAccordion from "../../components/calls/CallNoteAccordion.vue";
import {useModalStore} from "../../store/modal.js";

const route = useRoute()
const modalStore = useModalStore()
const ticketStore = useTicketStore()

const ticket = computed(() => ticketStore.ticket)
const order = computed(() => ticket.value?.reseller_order)

const client_contact = computed(() =>
    ticket.value?.client_solution?.client_contact
)

const reseller = computed(() =>
    order.value?.reseller?.name || order.value?.reseller?.company_name
)

const solution = computed(() =>
    order.value?.cart?.solution
)

const location = computed(() =>
    order.value?.cart?.address
)

const statusLabel = (status) => {
    switch (status) {
        case 'created':
            return 'Created'
        case 'assigned':
            return 'Assigned'
        case 'awaiting-external':
            return 'Awaiting external'
        case 'complete':
            return 'Complete'
        default:
            return status
    }
}

const back = () => {
    window.history.back()
}

const assignTicket = () => {
    modalStore.open(
        'AssignTicket',
        {id: ticket.value.id},
        'body',
        'bottom-right'
    )
}

const markAwaitingExternal = async () => {
    await ticketStore.markAwaitingExternal(ticket.value.id)
}

const completeTicket = () => {
    modalStore.open(
        'CompleteTicket',
        {id: ticket.value.id},
        'body',
        'bottom-right'
    )
}

const addNote = () => {
    modalStore.open(
        'TicketAddNote',
        {id: ticket.value.id},
        'body',
        'bottom-right'
    )
}

onMounted(async () => {
    await ticketStore.getTicket(route.params.id)
})
</script>

<template>
    <main v-if="!ticket">
        <empty-state
            icon="speechbubble-double"
            heading="Loading ticket."
            content="Give it sec, it's coming in a mo."
        />
    </main>

    <main v-else>
        <section>
            <!-- Header -->
            <div class="section-header">
                <div>
                    <a
                        class="back-button"
                        href="#"
                        @click.prevent="back"
                    >
                        <ah-icon name="arrow-left" />
                    </a>

                    <h1>#{{ ticket.external_id }}</h1>

                    <div class="status-badge">
                        {{ statusLabel(ticket.status) }}
                    </div>
                </div>

                <div>
                    <span
                        v-if="!order"
                        class="unlinked-badge"
                    >
                        Unlinked
                    </span>
                </div>
            </div>

            <!-- Actions -->
            <div class="section-header-actions">
                <BrandButton
                    variant="small"
                    v-if="ticket.external_url"
                    as="a"
                    :href="ticket.external_url"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <ah-icon name="arrow-in" />
                    <span>Open in Kayako</span>
                </BrandButton>

                <BrandButton
                    variant="small"
                    as="button"
                    @click="assignTicket"
                >
                    <ah-icon name="user-single-add" />
                    <span>{{ ticket.assignee_id ? 'Reassign' : 'Assign' }}</span>
                </BrandButton>

                <BrandButton
                    variant="small"
                    v-if="!['awaiting-external', 'complete'].includes(ticket.status)"
                    as="button"
                    @click="markAwaitingExternal"
                >
                    <ah-icon name="arrow-rotate" />
                    <span>Mark awaiting external</span>
                </BrandButton>

                <BrandButton
                    variant="small"
                    as="button"
                    @click="addNote"
                >
                    <ah-icon name="plus-default" />
                    <span>Add note</span>
                </BrandButton>

                <BrandButton
                    variant="primary"
                    v-if="ticket.status !== 'complete'"
                    as="button"
                    @click="completeTicket"
                >
                    <span>Complete</span>
                    <ah-icon
                        name="check-all"
                        color="var(--color-white)"
                    />
                </BrandButton>
            </div>

            <!-- Body -->
            <div class="section-body">

                <!-- Left column -->
                <div class="section-body-column">

                    <!-- Ticket information -->
                    <div class="section-card">
                        <div class="section-card-header">
                            <p>Ticket information.</p>
                        </div>

                        <div class="section-card-body">
                            <div class="section-card-body-row">
                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Created.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{ dayjs(ticket.created_at).format('DD MMM YYYY HH:mm') }}
                                    </p>
                                </div>

                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Kayako status.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{ ticket.external_status ?? '-' }}
                                    </p>
                                </div>
                            </div>

                            <div class="section-card-detail">
                                <p class="section-card-detail-label">
                                    Assigned to.
                                </p>

                                <p class="section-card-detail-value">
                                    {{
                                        ticket.assignee
                                            ? `${ticket.assignee.first_name} ${ticket.assignee.last_name}`
                                            : 'Unassigned'
                                    }}
                                </p>
                            </div>

                            <div class="ticket-content">
                                {{ ticket.content }}
                            </div>
                        </div>
                    </div>

                    <!-- Linked order -->
                    <div class="section-card">
                        <div class="section-card-header">
                            <p>Linked order.</p>
                        </div>

                        <div
                            class="section-card-body"
                            v-if="order"
                        >
                            <div class="section-card-body-row">
                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Client.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{
                                            client_contact
                                                ? `${client_contact.first_name ?? ''} ${client_contact.last_name ?? ''}`
                                                : '-'
                                        }}
                                    </p>
                                </div>

                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Reseller.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{ reseller ?? '-' }}
                                    </p>
                                </div>
                            </div>

                            <div class="section-card-body-row">
                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Cell nr.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{ client_contact?.cell_number ?? '-' }}
                                    </p>
                                </div>

                                <div class="section-card-detail">
                                    <p class="section-card-detail-label">
                                        Email.
                                    </p>

                                    <p class="section-card-detail-value">
                                        {{ client_contact?.email ?? '-' }}
                                    </p>
                                </div>
                            </div>

                            <router-link
                                class="section-card-detail"
                                :to="`/orders/view/${order.uuid}`"
                            >
                                <div class="section-card-detail-label">
                                    <p>Order.</p>
                                    <p class="order-uuid">{{ order.uuid }}</p>
                                </div>

                                <div class="section-card-detail-value view-link">
                                    View
                                    <ah-icon name="chevron-right" />
                                </div>
                            </router-link>
                        </div>

                        <div
                            class="section-card-body"
                            v-else
                        >
                            <empty-state
                                icon="speechbubble-double"
                                heading="No linked order."
                                :content="`No order matches client solution ${ticket.client_solution_id}.`"
                            />
                        </div>
                    </div>

                    <!-- Solution -->
                    <div
                        class="section-card"
                        v-if="solution"
                    >
                        <div class="section-card-header">
                            <ah-icon
                                :name="solution?.vendor_slug
                                    ? `provider-${solution.vendor_slug}`
                                    : 'provider-vuma-reach'"
                                :size="112"
                            />
                        </div>

                        <div class="section-card-body">

                            <div class="section-card-body-row">
                                <div class="section-card-detail speed-card">
                                    <div class="speed-card-details">
                                        <ah-icon name="arrow-up" />
                                        <span>
                                            {{
                                                Math.ceil(
                                                    solution?.attributes?.speed_upload / 1024
                                                )
                                            }}Mbps
                                        </span>
                                    </div>
                                </div>

                                <div class="section-card-detail speed-card">
                                    <div class="speed-card-details">
                                        <ah-icon name="arrow-down" />
                                        <span>
                                            {{
                                                Math.ceil(
                                                    solution?.attributes?.speed_download / 1024
                                                )
                                            }}Mbps
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="section-card-detail">
                                <p class="section-card-detail-label">
                                    Location.
                                </p>

                                <p class="section-card-detail-value">
                                    {{ location }}
                                </p>
                            </div>

                        </div>
                    </div>

                </div>

                <!-- Middle column -->
                <div class="section-body-column">

                    <!-- Order history -->
                    <div
                        class="section-card"
                        v-if="ticketStore.order_history"
                    >
                        <div class="section-card-header">
                            <p>Order history.</p>
                        </div>

                        <div class="section-card-body">
                            <order-history-day
                                :day="day"
                                :items="history"
                                v-for="(history, day) in ticketStore.order_history"
                                :key="day"
                            />
                        </div>
                    </div>

                </div>

                <!-- Right column -->
                <div class="section-body-column">

                    <!-- Notes -->
                    <div class="section-card">
                        <div class="section-card-header note-header-button">
                            <p>Notes.</p>

                            <button
                                class="notes-icon"
                                @click="addNote"
                            >
                                <ah-icon name="plus-default" />
                            </button>
                        </div>

                        <div class="section-card-body">
                            <call-note-accordion
                                :note="note.content"
                                :author="`${note?.created_by?.first_name} ${note?.created_by?.last_name}`"
                                :created_at="note.created_at"
                                v-for="note in ticket.notes"
                                :key="note.id"
                            />
                        </div>
                    </div>

                </div>

            </div>
        </section>
    </main>
</template>

<style scoped lang="scss">
.section-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;

    > div {
        display: flex;
        gap: 2rem;
        align-items: center;
    }

    .back-button {
        width: 3.6rem;
        height: 3.6rem;
        border-radius: 50%;
        background: var(--color-white);
        display: flex;
        justify-content: center;
        align-items: center;

        .icon {
            fill: var(--color-grey-hazy);
        }
    }

    .status-badge,
    .unlinked-badge {
        padding: 1.5rem 3rem;
        @include font(1.4, 1.4, black);
        border-radius: 3rem;
        border: .1rem solid var(--color-tiffany-dark);
        color: #009991;
        text-transform: capitalize;
        background: rgba(0, 152, 145, .2);
    }

    .unlinked-badge {
        border-color: var(--color-grey-hazy);
        color: var(--color-grey-hazy);
        background: transparent;
    }

    .brand-button {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    @include dark() {
        h1 {
            color: var(--color-white);
        }

        .back-button {
            background: var(--color-darth-maul);

            .icon {
                fill: var(--color-grey-dew);
            }
        }

        .status-badge,
        .unlinked-badge {
            color: var(--color-white);
        }

        .status-badge {
            background: rgba(0, 153, 145, .2);
        }
    }

    @include mobile() {
        flex-direction: column;
        padding: 0;
        gap: 2rem;

        > div {
            width: 100%;
        }

        .brand-button,
        .status-badge,
        .unlinked-badge {
            width: 100%;
            justify-content: center;
        }

        .brand-button {
            height: 6rem;
        }
    }
}

.section-header-actions {
    display: flex;
    gap: 1rem;
    margin-bottom: 4rem;

    .brand-button {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    @include dark() {
        .brand-button {
            .icon {
                fill: var(--color-white-100) !important;
            }
        }
    }

    @include mobile() {
        flex-direction: column;
    }
}

.section-body {
    display: flex;
    gap: 2rem;

    .section-body-column {
        flex: 1;

        .section-card {
            background: var(--color-white);
            padding: 1rem;
            border-radius: 2.4rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 2rem;

            .section-card-header {
                min-height: 8.4rem;
                display: flex;
                align-items: center;
                justify-content: center;
                background: var(--color-grey-dew);
                border-radius: 2.4rem;
                position: relative;

                p {
                    @include font(2.4, 2.4, black);
                }

                .icon {
                    width: 11.2rem;
                    height: 8rem;
                }
            }

            .section-card-body {
                display: flex;
                flex-direction: column;
                gap: 1rem;

                .section-card-body-row {
                    display: flex;
                    align-items: center;
                    gap: 2rem;

                    > div {
                        flex: 1;
                        min-width: 0;
                    }
                }

                .section-card-detail {
                    padding: 1.2rem 2rem;
                    border-radius: 2.4rem;
                    background: var(--color-grey-mist);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 2rem;
                    min-height: 6rem;

                    p {
                        @include font(1.4, 2, book);
                    }

                    .section-card-detail-label {
                        @include font(1.4, 2, book);
                        min-width: 0;
                    }

                    .section-card-detail-value {
                        @include font(1.4, 2, black);
                        min-width: 0;
                        text-align: right;
                        overflow-wrap: anywhere;
                    }
                }

                .speed-card {
                    justify-content: center;

                    .speed-card-details {
                        display: flex;
                        gap: 1rem;
                        align-items: center;

                        .icon {
                            fill: var(--color-tiffany-dark);
                        }

                        span {
                            @include font(1.4, 1.4, black);
                        }
                    }
                }
            }
        }
    }

    @include dark() {
        .section-card {
            background: var(--color-darth-maul);
            color: var(--color-white) !important;

            .section-card-header {
                background: var(--color-darth-vader);

                p {
                    color: var(--color-white);
                }

                .icon {
                    fill: var(--color-white);
                }
            }

            .section-card-body {
                .section-card-detail {
                    background: var(--color-darth-sidious);

                    p,
                    .section-card-detail-label,
                    .section-card-detail-value {
                        color: var(--color-white);
                    }
                }
            }
        }
    }

    @include mobile() {
        flex-direction: column;

        .section-body-column {
            .section-card {
                .section-card-body {
                    .section-card-body-row {
                        flex-direction: column;
                        align-items: unset;

                        > div {
                            width: 100%;
                        }
                    }

                    .section-card-detail {
                        min-height: 6rem;
                        height: unset;
                        gap: 2rem;
                        padding: 2rem;
                    }
                }
            }
        }
    }
}

.ticket-content {
    padding: 2rem;
    border-radius: 2.4rem;
    //background: var(--color-grey-mist);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    @include font(1.4, 2.2, book);

    @include dark() {
        color: var(--color-white);
    }
}

.note-header-button {
    .notes-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 3.6rem;
        height: 3.6rem;
        border-radius: 50%;
        border: none;
        right: 2.8rem;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon {
            width: 2.4rem;
            height: 2.4rem;
        }

        &:hover {
            background: var(--color-raspberry);

            .icon {
                fill: var(--color-white);
            }
        }

        @include dark() {
            background: var(--color-darth-vader);
        }
    }
}

.order-uuid {
    @include font(1.2, 1.8, book);
    overflow-wrap: anywhere;
    color: var(--color-grey-hazy);
}

.view-link {
    display: flex;
    align-items: center;
    gap: .5rem;
    color: var(--color-grey-hazy);

}

</style>
